import React, { useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faSpinner,
  faTimes,
  faArrowUpRightFromSquare,
  faBell,
} from '@fortawesome/free-solid-svg-icons';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { marketDataApi } from '../api/marketDataApi';
import { useQuote } from '../contexts/QuoteContext';
import { useAlerts } from '../contexts/AlertsContext';
import { EarningsBadge } from '../contexts/EarningsBookContext';
import OrderForm from './OrderForm';
import SymbolStickyNote from './SymbolStickyNote';
import MutualFundPanel from './MutualFundPanel';

const formatCurrency = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 4,
  }).format(Number(value));
};

const formatSignedCurrency = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const amount = formatCurrency(Math.abs(Number(value)));
  if (Number(value) > 0) return `+${amount}`;
  if (Number(value) < 0) return `-${amount}`;
  return amount;
};

const formatSignedPercent = (value) => {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return '—';
  const n = Number(value);
  const sign = n > 0 ? '+' : n < 0 ? '' : '';
  return `${sign}${n.toFixed(2)}%`;
};

function instrumentKind(assetType) {
  const t = String(assetType || '').toUpperCase();
  if (t === 'MUTUAL_FUND') return 'fund';
  if (t === 'ETF') return 'etf';
  if (t === 'OPTION' || t === 'OPTIONS') return 'option';
  return 'stock';
}

const CHART_RANGES = {
  '1D': { label: '1D', timeframe: '5Min', days: 1, limit: 120 },
  '5D': { label: '5D', timeframe: '15Min', days: 5, limit: 200 },
};

const QuoteMiniChart = ({ symbol, compact }) => {
  const [range, setRange] = useState('1D');
  const [points, setPoints] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!symbol) return undefined;
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const cfg = CHART_RANGES[range] || CHART_RANGES['1D'];
        const end = new Date(Date.now() - 60 * 60 * 1000);
        const start = new Date(end);
        start.setDate(start.getDate() - cfg.days);
        const response = await marketDataApi.getBars(symbol, {
          timeframe: cfg.timeframe,
          start: start.toISOString(),
          end: end.toISOString(),
          limit: cfg.limit,
        });
        if (cancelled) return;
        const bars = Array.isArray(response.data) ? response.data : [];
        const mapped = bars
          .map((bar) => {
            const timestamp = bar.timestamp || bar.t || bar.time || bar.date;
            const close = bar.close ?? bar.c;
            return {
              t: timestamp,
              close: close != null ? Number(close) : null,
            };
          })
          .filter((p) => p.t && Number.isFinite(p.close))
          .sort((a, b) => new Date(a.t) - new Date(b.t));
        setPoints(mapped);
      } catch (err) {
        if (!cancelled) {
          setPoints([]);
          setError(err.response?.data?.error || err.message || 'Chart unavailable');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [symbol, range]);

  const stroke = useMemo(() => {
    if (points.length < 2) return 'var(--lt-action)';
    return points[points.length - 1].close >= points[0].close
      ? 'var(--lt-bull)'
      : 'var(--lt-bear)';
  }, [points]);

  const domain = useMemo(() => {
    if (!points.length) return ['auto', 'auto'];
    const vals = points.map((p) => p.close);
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const pad = (max - min) * 0.08 || max * 0.002 || 0.01;
    return [min - pad, max + pad];
  }, [points]);

  return (
    <div className="rounded-md border border-hairline bg-canvas overflow-hidden">
      <div className="flex items-center justify-between gap-2 px-2.5 pt-1.5 pb-0.5">
        <div className="lt-label">Price</div>
        <div className="inline-flex rounded border border-hairline overflow-hidden">
          {Object.keys(CHART_RANGES).map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => setRange(key)}
              className={`h-6 px-2 text-[10px] font-semibold uppercase tracking-wider ${
                range === key ? 'bg-action text-white' : 'text-muted hover:bg-elevated'
              }`}
            >
              {CHART_RANGES[key].label}
            </button>
          ))}
        </div>
      </div>
      <div className={`${compact ? 'h-[88px]' : 'h-[112px]'} px-1 pb-1 relative`}>
        {loading && (
          <div className="absolute inset-0 flex items-center justify-center z-10">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-muted text-sm" />
          </div>
        )}
        {error && !loading ? (
          <div className="h-full flex items-center justify-center text-[11px] text-muted px-3 text-center">
            {error}
          </div>
        ) : points.length === 0 && !loading ? (
          <div className="h-full flex items-center justify-center text-[11px] text-muted">No bars</div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={points} margin={{ top: 4, right: 6, left: 0, bottom: 0 }}>
              <XAxis dataKey="t" hide />
              <YAxis domain={domain} hide width={0} />
              <Tooltip
                contentStyle={{
                  background: 'var(--lt-surface)',
                  border: '1px solid var(--lt-hairline)',
                  borderRadius: 6,
                  fontSize: 11,
                }}
                labelFormatter={(value) => {
                  try {
                    return new Date(value).toLocaleString();
                  } catch {
                    return value;
                  }
                }}
                formatter={(value) => [formatCurrency(value), 'Close']}
              />
              <Line
                type="monotone"
                dataKey="close"
                stroke={stroke}
                strokeWidth={1.75}
                dot={false}
                isAnimationActive={false}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>
    </div>
  );
};

function MetricCell({ label, value, sub, valueClass = 'text-ink' }) {
  return (
    <div className="min-w-0 px-2.5 py-1.5">
      <div className="lt-label mb-0.5">{label}</div>
      <div className={`num text-[15px] font-semibold leading-tight ${valueClass}`}>{value}</div>
      {sub != null && <div className="text-[10px] text-muted mt-0.5 truncate">{sub}</div>}
    </div>
  );
}

const QuotePanel = () => {
  const { symbol, asset, selectSymbol, notifyOrderSubmitted } = useQuote();
  const { addAlert } = useAlerts();
  const [quote, setQuote] = useState(null);
  const [dayBar, setDayBar] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [side, setSide] = useState('buy');
  const [alertMsg, setAlertMsg] = useState('');

  const kind = instrumentKind(asset?.assetType);
  const isFund = kind === 'fund';

  useEffect(() => {
    if (!symbol) {
      setQuote(null);
      setDayBar(null);
      setError('');
      setSide('buy');
      setAlertMsg('');
      return undefined;
    }

    let cancelled = false;

    const loadQuote = async (showSpinner = false) => {
      try {
        if (showSpinner) setLoading(true);
        const response = await marketDataApi.getQuote(symbol);
        if (cancelled) return;
        setQuote(response.data || null);
        setError('');
      } catch (err) {
        if (cancelled) return;
        setQuote(null);
        // Funds often lack live NBBO — don't treat as hard error
        if (!isFund) {
          setError(err.response?.data?.error || err.message || 'Quote unavailable');
        } else {
          setError('');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    const loadDayBar = async () => {
      try {
        const response = await marketDataApi.getBars(symbol, {
          timeframe: '1Day',
          limit: 2,
        });
        if (cancelled) return;
        const bars = response.data || [];
        const latest = bars[bars.length - 1] || null;
        const previous = bars.length > 1 ? bars[bars.length - 2] : null;
        setDayBar(
          latest
            ? {
                open: latest.open,
                close: latest.close,
                prevClose: previous?.close ?? null,
              }
            : null
        );
      } catch {
        if (!cancelled) setDayBar(null);
      }
    };

    loadQuote(true);
    if (!isFund) loadDayBar();
    const quoteInterval = isFund ? null : setInterval(() => loadQuote(false), 2000);
    const barInterval = isFund ? null : setInterval(loadDayBar, 30000);
    return () => {
      cancelled = true;
      if (quoteInterval) clearInterval(quoteInterval);
      if (barInterval) clearInterval(barInterval);
    };
  }, [symbol, isFund]);

  useEffect(() => {
    if (!symbol) return undefined;
    const onKeyDown = (event) => {
      if (event.key === 'Escape') {
        selectSymbol('');
      }
    };
    window.addEventListener('keydown', onKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [symbol, selectSymbol]);

  if (!symbol) return null;

  const bid = quote?.bidPrice;
  const ask = quote?.askPrice;
  const mid = bid != null && ask != null ? (Number(bid) + Number(ask)) / 2 : null;
  const spread = bid != null && ask != null ? Number(ask) - Number(bid) : null;
  const last = mid != null ? Number(mid) : dayBar?.close != null ? Number(dayBar.close) : null;
  const baseline =
    dayBar?.prevClose != null && Number(dayBar.prevClose) > 0
      ? Number(dayBar.prevClose)
      : dayBar?.open != null && Number(dayBar.open) > 0
        ? Number(dayBar.open)
        : null;
  const dayChange = last != null && baseline != null ? last - baseline : null;
  const dayChangePercent =
    dayChange != null && baseline ? (dayChange / baseline) * 100 : null;
  const dayPositive = dayChange == null ? null : dayChange >= 0;
  const dayColor =
    dayPositive == null ? 'text-muted' : dayPositive ? 'text-bull' : 'text-bear';

  const typeBadge =
    kind === 'fund'
      ? { label: 'Fund', className: 'bg-amber-100 text-amber-900' }
      : kind === 'etf'
        ? { label: 'ETF', className: 'bg-sky-100 text-sky-900' }
        : kind === 'option'
          ? { label: 'Option', className: 'bg-violet-100 text-violet-900' }
          : { label: 'Stock', className: 'bg-elevated text-muted border border-hairline' };

  const close = () => selectSymbol('');

  const handleQuickAlert = async () => {
    const price = mid != null ? mid : last;
    if (price == null || !Number.isFinite(Number(price))) {
      setAlertMsg('Wait for a quote price');
      return;
    }
    const created = await addAlert({
      symbol,
      type: 'price_above',
      threshold: Number(Number(price).toFixed(2)),
    });
    if (created) {
      setAlertMsg(`Alert: ${symbol} ≥ $${created.threshold}`);
      setTimeout(() => setAlertMsg(''), 2500);
    }
  };

  const modal = (
    <div className="fixed inset-0 z-[80] flex items-end sm:items-center justify-center p-0 sm:p-3">
      <button
        type="button"
        className="absolute inset-0 bg-[#0B0E14]/70 backdrop-blur-[2px]"
        aria-label="Close quote"
        onClick={close}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="quote-dialog-title"
        className="relative w-full sm:max-w-[440px] max-h-[90vh] overflow-y-auto rounded-t-xl sm:rounded-xl border border-hairline bg-surface shadow-[0_24px_80px_rgba(0,0,0,0.45)]"
      >
        {/* Header */}
        <div className="sticky top-0 z-10 border-b border-hairline bg-surface/95 backdrop-blur-md px-3 pt-3 pb-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <div className="flex items-center gap-1.5 min-w-0 flex-wrap">
                <h2 id="quote-dialog-title" className="num text-lg font-semibold text-ink leading-none">
                  {symbol}
                </h2>
                <span
                  className={`px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider rounded-sm ${typeBadge.className}`}
                >
                  {typeBadge.label}
                </span>
                <EarningsBadge symbol={symbol} />
                {loading && <FontAwesomeIcon icon={faSpinner} className="animate-spin text-muted text-[10px]" />}
              </div>
              {asset?.name && (
                <div className="text-[11px] text-muted truncate mt-0.5">{asset.name}</div>
              )}
              {!isFund && dayChange != null && (
                <div className={`mt-1 num text-[12px] font-semibold ${dayColor}`}>
                  {formatCurrency(last)}
                  <span className="ml-1.5">
                    {formatSignedCurrency(dayChange)} ({formatSignedPercent(dayChangePercent)})
                  </span>
                </div>
              )}
            </div>
            <div className="flex items-center gap-0.5 shrink-0">
              {!isFund && (
                <button
                  type="button"
                  onClick={handleQuickAlert}
                  className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:bg-elevated hover:text-ink"
                  title="Alert at current mid"
                >
                  <FontAwesomeIcon icon={faBell} className="text-[11px]" />
                </button>
              )}
              <Link
                to={`/asset/${encodeURIComponent(symbol)}`}
                className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:bg-elevated hover:text-ink"
                title="Open full asset page"
                onClick={close}
              >
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} className="text-[11px]" />
              </Link>
              <button
                type="button"
                onClick={close}
                className="h-7 w-7 inline-flex items-center justify-center rounded-md border border-hairline text-muted hover:bg-elevated hover:text-ink"
                title="Close quote"
              >
                <FontAwesomeIcon icon={faTimes} className="text-[11px]" />
              </button>
            </div>
          </div>
          {alertMsg && <div className="mt-1.5 text-[11px] text-bull">{alertMsg}</div>}
        </div>

        <div className="px-3 py-2.5 space-y-2.5">
          {/* Stock / ETF: compact L1 strip */}
          {!isFund && (
            error ? (
              <div className="text-xs text-bear">{error}</div>
            ) : (
              <div className="rounded-md border border-hairline bg-canvas grid grid-cols-4 divide-x divide-[var(--lt-border)]">
                <MetricCell
                  label="Bid"
                  value={formatCurrency(bid)}
                  sub={quote?.bidSize != null ? `×${Number(quote.bidSize).toLocaleString()}` : null}
                  valueClass="text-bear"
                />
                <MetricCell label="Mid" value={formatCurrency(mid)} />
                <MetricCell
                  label="Ask"
                  value={formatCurrency(ask)}
                  sub={quote?.askSize != null ? `×${Number(quote.askSize).toLocaleString()}` : null}
                  valueClass="text-bull"
                />
                <MetricCell label="Sprd" value={formatCurrency(spread)} />
              </div>
            )
          )}

          {/* Mutual fund: NAV + fundamentals (replaces L1) */}
          {isFund && (
            <MutualFundPanel
              symbol={symbol}
              assetType={asset?.assetType}
              variant="quote"
              hideDuplicateHeader
            />
          )}

          {/* Intraday chart — stocks/ETFs only (funds are EOD NAV) */}
          {!isFund && <QuoteMiniChart symbol={symbol} compact />}

          <SymbolStickyNote symbol={symbol} />

          <div className="grid grid-cols-2 gap-1.5">
            <button
              type="button"
              onClick={() => setSide('buy')}
              className={`h-9 rounded-md text-xs font-semibold uppercase tracking-[0.12em] ${
                side === 'buy'
                  ? 'bg-bull text-[#0B0E14]'
                  : 'border border-hairline text-muted hover:bg-elevated'
              }`}
            >
              {isFund ? 'Buy shares' : 'Buy'}
            </button>
            <button
              type="button"
              onClick={() => setSide('sell')}
              className={`h-9 rounded-md text-xs font-semibold uppercase tracking-[0.12em] ${
                side === 'sell'
                  ? 'bg-bear text-white'
                  : 'border border-hairline text-muted hover:bg-elevated'
              }`}
            >
              {isFund ? 'Sell shares' : 'Sell'}
            </button>
          </div>

          {isFund && (
            <p className="text-[10px] text-muted leading-snug">
              Mutual funds typically trade at end-of-day NAV — no live bid/ask.
            </p>
          )}

          <OrderForm
            symbol={symbol}
            initialSide={side}
            lockSymbol
            hideQuoteStrip
            defaultAutoRefresh
            variant="modal"
            onOrderSubmitted={(order) => {
              notifyOrderSubmitted(order);
            }}
          />
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
};

export default QuotePanel;
